import React, { useState, useEffect } from 'react';
import { BookOpen, Search, Plus, Trash2, Menu, ChevronLeft, ChevronRight } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { auth } from '../lib/firebase';
import { supabase } from '../lib/supabase';
import { useStore } from '../lib/store';

interface Course {
  id: string;
  codigo: string;
  nome: string;
}

const ITEMS_PER_PAGE = 10;

export function Fields() {
  const { courses, fetchCourses } = useStore();
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Resetar página quando mudar a busca
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!newCode || !newName) {
        throw new Error('Preencha todos os campos');
      }

      if (courses.some(course => course.codigo === newCode)) {
        throw new Error('Este código já está cadastrado');
      }

      const { data, error: insertError } = await supabase
        .from('cursos')
        .insert([{ 
          codigo: newCode, 
          nome: newName 
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Limpar campos
      setNewCode('');
      setNewName('');
      
      // Atualizar lista de cursos
      await fetchCourses();

      // Feedback visual
      const form = e.target as HTMLFormElement;
      form.reset();
      
    } catch (err) {
      console.error('Erro ao adicionar curso:', err);
      setError(err instanceof Error ? err.message : 'Erro ao adicionar curso');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      const { error: deleteError } = await supabase
        .from('cursos')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Atualizar lista após exclusão
      await fetchCourses();
    } catch (err) {
      console.error('Erro ao excluir curso:', err);
      setError('Erro ao excluir curso');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => 
    course.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Cálculos para paginação
  const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCourses = filteredCourses.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Função para mudar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen bg-[#111318]">
      <Sidebar
        user={auth.currentUser}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
      />

      <button
        onClick={() => setMenuOpen(true)}
        className={`fixed top-4 left-4 z-20 p-2 text-white rounded-lg bg-[#26428C] ${menuOpen ? 'hidden' : 'block'}`}
      >
        <Menu className="w-6 h-6" />
      </button>

      <div className={`flex-1 transition-all duration-200 ${menuOpen ? 'ml-64' : 'ml-0'}`}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-8">
            <BookOpen className="w-8 h-8 text-[#26428C]" />
            <h1 className="text-3xl font-bold text-white">Campos</h1>
          </div>

          <div className="grid gap-6">
            <div className="bg-[#1a1d23] rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-white">
                <Plus className="w-5 h-5" />
                Adicionar Curso
              </h2>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-400 mb-1">
                    Código do Curso
                  </label>
                  <input
                    id="code"
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    className="w-full px-4 py-2 bg-[#111318] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#26428C] focus:border-transparent"
                    placeholder="Ex: 4"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-1">
                    Nome do Curso
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-4 py-2 bg-[#111318] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#26428C] focus:border-transparent"
                    placeholder="Ex: Alfabetização"
                  />
                </div>

                {error && (
                  <div className="col-span-2 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="col-span-2 py-2 px-4 bg-[#26428C] text-white rounded-lg font-medium hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#26428C] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adicionando...' : 'Adicionar Curso'}
                </button>
              </form>
            </div>

            <div className="bg-[#1a1d23] rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Cursos Cadastrados</h2>
                
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar curso..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-[#111318] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#26428C] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="divide-y divide-gray-700">
                {paginatedCourses.map((course) => (
                  <div key={course.id} className="py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{course.nome}</p>
                      <p className="text-sm text-gray-400">Código: {course.codigo}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(course.id)}
                      disabled={loading}
                      className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remover curso"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {filteredCourses.length === 0 && (
                  <div className="py-4 text-center text-gray-400">
                    Nenhum curso encontrado
                  </div>
                )}

                {/* Paginação */}
                {filteredCourses.length > 0 && (
                  <div className="pt-4 flex items-center justify-between border-t border-gray-700">
                    <div className="text-sm text-gray-400">
                      Mostrando {startIndex + 1} a {Math.min(startIndex + ITEMS_PER_PAGE, filteredCourses.length)} de {filteredCourses.length} cursos
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 text-white rounded-lg hover:bg-[#26428C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            const nearCurrent = Math.abs(page - currentPage) <= 1;
                            return page === 1 || page === totalPages || nearCurrent;
                          })
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span className="text-gray-600 px-2">...</span>
                              )}
                              <button
                                onClick={() => handlePageChange(page)}
                                className={`min-w-[32px] h-8 flex items-center justify-center rounded-lg transition-colors ${
                                  currentPage === page
                                    ? 'bg-[#26428C] text-white'
                                    : 'text-gray-400 hover:bg-[#26428C] hover:text-white'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 text-white rounded-lg hover:bg-[#26428C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}